/**
 * In-app Help & Getting Started guide. Mirrors the branded PDF
 * (public/develio-onboarding-guide.pdf) so a new EDC director can follow the
 * onboarding flow without leaving the app, and download the PDF to share.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

const PDF_URL = '/develio-onboarding-guide.pdf';

interface Step {
  n: number;
  title: string;
  body: ReactNode;
}

const STEPS: Step[] = [
  {
    n: 1,
    title: 'Create your account',
    body: (
      <ol className="list-decimal space-y-1 pl-5">
        <li>
          Go to <strong>develio.com</strong> and click <strong>Get Started</strong>.
        </li>
        <li>Sign up with your email, or with Google or GitHub.</li>
        <li>
          When prompted, <strong>create your organization</strong>. Name it after your EDC. This is
          the workspace your whole team shares.
        </li>
      </ol>
    ),
  },
  {
    n: 2,
    title: 'Start your free trial',
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong>You are not charged today.</strong> The card is on file so your subscription can
          begin automatically when the trial ends.
        </li>
        <li>
          <strong>Pick monthly or annual.</strong> Professional is $299/month, or $2,990/year (two
          months free).
        </li>
        <li>
          <strong>The trial includes</strong> full Professional access: 2 complete impact analyses
          and all market intelligence.
        </li>
        <li>
          <strong>Billing starts</strong> on day 15 unless you cancel first. Cancel anytime under
          Settings &rarr; Billing.
        </li>
      </ul>
    ),
  },
  {
    n: 3,
    title: 'The 30-second setup',
    body: (
      <p>
        The first time you sign in, Develio asks for your <strong>organization name</strong>,{' '}
        <strong>primary city</strong>, <strong>state</strong>, and <strong>ZIP</strong>. It then
        finds your county and loads a starter jurisdiction with estimated rates so you can run an
        analysis right away. These starting numbers are estimates you refine later (Step 8).
      </p>
    ),
  },
  {
    n: 4,
    title: 'Get your bearings (the dashboard)',
    body: (
      <p>
        Click <strong>Take a tour</strong> for a 60-second guided walkthrough. The dashboard cards
        show your workspace, how many analyses you have run, and quick actions to start one or open
        your jurisdiction. A yellow &ldquo;estimated data&rdquo; notice is normal for a new account.
      </p>
    ),
  },
  {
    n: 5,
    title: 'Run your first impact analysis',
    body: (
      <>
        <p className="mb-2">
          Click <strong>New Analysis</strong> to open the guided wizard. It is 6 short steps:
        </p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            <strong>Project</strong> &mdash; name it and pick its type.
          </li>
          <li>
            <strong>Capital Investment</strong> &mdash; buildings and equipment.
          </li>
          <li>
            <strong>Employment</strong> &mdash; jobs and average wages.
          </li>
          <li>
            <strong>Revenue</strong> &mdash; expected annual sales or output.
          </li>
          <li>
            <strong>Property</strong> &mdash; taxable value of new property.
          </li>
          <li>
            <strong>Incentives</strong> &mdash; any incentive you are considering.
          </li>
        </ol>
        <p className="mt-2 text-slate-500">
          Then click <strong>Run Analysis</strong>. Jurisdiction and Assumptions are optional
          advanced sections; by default each analysis uses your saved settings.
        </p>
      </>
    ),
  },
  {
    n: 6,
    title: 'Read your report',
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong>The bottom line:</strong> whether the project is a net positive, and by how much.
        </li>
        <li>
          <strong>Jobs:</strong> direct, plus the indirect and induced jobs supported.
        </li>
        <li>
          <strong>Tax revenue</strong> across your taxing entities, and{' '}
          <strong>return on incentive</strong>.
        </li>
        <li>
          <strong>Download a branded PDF</strong> with an AI-written summary and citations for your
          board.
        </li>
      </ul>
    ),
  },
  {
    n: 7,
    title: 'Explore your market intelligence',
    body: (
      <p>
        Open <strong>Market Intelligence</strong> in the sidebar for ready-to-cite, federally
        sourced profiles of your area: <strong>Community Health</strong>,{' '}
        <strong>Workforce Profile</strong>, <strong>Industry Targets</strong>, and{' '}
        <strong>Wage Benchmarks</strong>. A fast way to answer &ldquo;is this a good fit for
        us?&rdquo; before you run the numbers.
      </p>
    ),
  },
  {
    n: 8,
    title: 'Make the numbers your own',
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong>Jurisdiction:</strong> replace estimated rates with your community&rsquo;s actual
          rates. Do it once and every future analysis reflects it.
        </li>
        <li>
          <strong>RIMS Multipliers:</strong> load your region&rsquo;s multipliers, or ask your
          Develio contact to load them for you.
        </li>
        <li>
          <strong>Members:</strong> invite your colleagues into the workspace.
        </li>
      </ul>
    ),
  },
];

export function HelpRoute() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-16">
      {/* Header + PDF download */}
      <header className="flex flex-col gap-4 rounded-xl border border-brand-teal-200 bg-gradient-to-br from-brand-teal-50 to-white p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-teal-700">
            Getting Started
          </p>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            From sign-up to your first impact report in 15 minutes
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            A plain-English walkthrough. No technical background needed.
          </p>
        </div>
        <a
          href={PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded-lg bg-brand-teal-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-brand-teal-700"
        >
          Download PDF guide
        </a>
      </header>

      {/* What it does */}
      <section className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900">What Develio does, in one sentence</h3>
        <p className="text-slate-600">
          You describe a development project, and Develio turns it into a defensible fiscal impact
          report: jobs created, tax revenue generated, and return on any incentive you offer, backed
          by federal data you can cite to your board.
        </p>
        <div className="rounded-lg border-l-4 border-brand-teal-400 bg-brand-teal-50 px-4 py-3 text-sm text-slate-600">
          You do not need any tax tables, spreadsheets, or data files to get going. Develio
          pre-loads sensible estimates and you refine them later.
        </div>
      </section>

      {/* Steps */}
      <section className="space-y-5">
        {STEPS.map((s) => (
          <div key={s.n} className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-develio-green text-sm font-bold text-white">
              {s.n}
            </div>
            <div className="flex-1">
              <h3 className="mb-1 text-base font-semibold text-slate-900">{s.title}</h3>
              <div className="text-sm text-slate-600">{s.body}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Trial conversion */}
      <section className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <h3 className="text-base font-semibold text-slate-900">
          When your trial becomes a subscription
        </h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
          <li>
            The trial includes <strong>2 full analyses</strong>. Starting a 3rd invites you to begin
            your subscription for unlimited.
          </li>
          <li>
            If you do nothing, your subscription <strong>begins automatically on day 15</strong> and
            analyses become unlimited.
          </li>
          <li>
            To start early or cancel before any charge, go to{' '}
            <strong>Settings &rarr; Billing</strong>.
          </li>
        </ul>
      </section>

      {/* Troubleshooting */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Quick troubleshooting</h3>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="w-1/2 bg-slate-50 px-4 py-3 font-medium text-slate-700">
                  Every page sends you to the payment screen
                </td>
                <td className="px-4 py-3 text-slate-600">
                  Your active workspace has not started its trial. Use the organization switcher at
                  the top of that screen to pick the right workspace.
                </td>
              </tr>
              <tr>
                <td className="bg-slate-50 px-4 py-3 font-medium text-slate-700">
                  A yellow &ldquo;estimated data&rdquo; notice
                </td>
                <td className="px-4 py-3 text-slate-600">
                  Expected for a new account. Refine your Jurisdiction before sharing reports
                  externally.
                </td>
              </tr>
              <tr>
                <td className="bg-slate-50 px-4 py-3 font-medium text-slate-700">
                  You cannot find SSO or methodology locking
                </td>
                <td className="px-4 py-3 text-slate-600">
                  Those are Enterprise features. Talk to your Develio contact about upgrading.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Cross-link to the methodology guide */}
      <Link
        to="/help/methodology"
        className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5 transition hover:border-brand-teal-300 hover:bg-brand-teal-50/40"
      >
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Want to know how the numbers are calculated?
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Read the methodology: our approach, the calculations, and how we use federal BEA RIMS II
            data. Built to stand up in front of a board.
          </p>
        </div>
        <span className="shrink-0 text-sm font-medium text-brand-teal-700">Methodology &rarr;</span>
      </Link>

      <p className="text-center text-sm text-slate-400">
        Prefer a printable copy?{' '}
        <a
          href={PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-brand-teal-700 underline"
        >
          Download the PDF guide
        </a>
        .
      </p>
    </div>
  );
}
